import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { AlertCircle, Check, X, FolderOpen, Trash2, ArrowRight, Folder } from "lucide-react";
import { Room } from "../../models/Room";
import { UIRoomItem, RoomItem } from '../../models/RoomItem';
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { useState, useEffect, useMemo } from "react";
import { Trans, t } from '@lingui/macro';
import { useToast } from "../../components/ui/use-toast";
import { FileIconList } from "./MessageFileItem";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Util } from '../../services/Utils/Util';
import { Badge } from "../../components/ui/badge";

export type ActionType = 'delete' | 'link' | 'move';

interface CheckedItemModalProps {
  show: boolean;
  actionType: ActionType;
  setShow: (v: boolean) => void;
  room: Room;
  topFolderItems: UIRoomItem[];
  topFileItems: UIRoomItem[];
  childItems: { [key: string]: UIRoomItem[] };
  folderItems: UIRoomItem[];
}

export const CheckedItemModal: React.FC<CheckedItemModalProps> = ({
  show, actionType, setShow, room, topFolderItems, topFileItems, childItems, folderItems
}) => {
  const { toast } = useToast();
  const [step, setStep] = useState<'list' | 'folder-select'>('list');

  const { itemsWithPermission, validItems } = useMemo(() => {
    const valid: UIRoomItem[] = [];
    // Flatten items
    let flatItems = [...topFolderItems];
    Object.values(childItems).forEach(childs => flatItems.push(...childs));
    flatItems.push(...topFileItems);

    const processed = flatItems.map(uitem => {
      const isOwner = uitem.item.user.id === My.user.id;
      const isCreator = room.creatorId === My.user.id;
      // Default permission check: Owner or Room Creator
      let hasPermission = isOwner || isCreator;

      // Update UIRoomItem property for consistency, though we use local var for logic
      if (actionType === 'delete') uitem.isDeletable = hasPermission;
      else uitem.isMoveable = hasPermission;

      if (hasPermission) valid.push(uitem);
      return { uitem, hasPermission };
    });

    return { itemsWithPermission: processed, validItems: valid };
  }, [topFolderItems, topFileItems, childItems, actionType, room.creatorId]);

  useEffect(() => {
    if (show) {
      setStep('list');
    }
  }, [show]);

  const handleAction = async () => {
    if (validItems.length === 0) {
      toast({ title: t`No valid items to process.`, duration: 3000 });
      return;
    }

    if (actionType === 'delete') {
      await Fire.item.deleteRoomItems(validItems.map(i => i.item));
      toast({ title: t`${validItems.length} items deleted` });
      setShow(false);
    } else if (actionType === 'move') {
      setStep('folder-select');
    }
  };

  const handleMoveToFolder = async (targetFolder: UIRoomItem | undefined) => {
    // If targetFolder is undefined, it means move to Top
    const targetId = targetFolder ? targetFolder.item.id : "";

    // Filter out items that are already in the target folder?
    // Doing it simple: just update parentId
    const itemsToSave: RoomItem[] = [];
    validItems.forEach(uitem => {
      // Avoid moving folder into itself or its children - Basic check
      if (uitem.item.id === targetId) return;

      uitem.item.parentId = targetId;
      itemsToSave.push(uitem.item);
    });

    if (itemsToSave.length > 0) {
      await Fire.item.saveRoomItems(itemsToSave);
      toast({ title: t`${itemsToSave.length} items moved` });
      setShow(false);
    }
  };

  return (
    <Dialog open={show} onOpenChange={setShow}>
      <DialogContent className="sm:max-w-xl max-h-[80vh] flex flex-col p-0 gap-0 overflow-hidden">
        {step === 'list' && (
          <>
            <DialogHeader className="p-6 pb-2">
              <DialogTitle className="flex items-center gap-2 text-warning font-semibold">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                {actionType === 'move' ? (
                  <Trans>Contains files that cannot be moved</Trans>
                ) : (
                  <Trans>Contains files that cannot be deleted</Trans>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-2">
              <div className="flex flex-col gap-2">
                {itemsWithPermission.map(({ uitem, hasPermission }) => {
                  const item = uitem.item;

                  return (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className="shrink-0 text-center w-6">
                          {hasPermission ? <Check className="w-5 h-5 text-green-500" /> : <X className="w-5 h-5 text-red-500" />}
                        </div>

                        <div className="shrink-0 w-8 h-8 flex items-center justify-center">
                          <FileIconList uitem={uitem} />
                        </div>

                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate">{item.title || item.url}</span>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Avatar className="w-4 h-4">
                              <AvatarImage src={item.user.photoUrl ?? Util.ANONYMOUS_PHOTO_URL} />
                              <AvatarFallback>{item.user.name?.substring(0, 1) || '?'}</AvatarFallback>
                            </Avatar>
                            <span className="truncate">{item.user.name}</span>
                            {item.pinned && <Badge variant="outline" className="text-[10px] h-4 px-1 text-red-500 border-red-200">Pinned</Badge>}
                          </div>
                        </div>
                      </div>

                      {!hasPermission && (
                        <div className="shrink-0 ml-2">
                          <Badge variant="secondary" className="text-xs bg-red-50 text-red-600 border-red-100 pointer-events-none whitespace-nowrap">
                            <Trans>No Permission</Trans>
                          </Badge>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <DialogFooter className="p-4 border-t bg-gray-50/50">
              <div className="flex justify-between items-center w-full">
                <span className="text-sm text-gray-500">
                  {actionType === 'delete' ? t`Deletable:` : t`Movable:`} <b>{validItems.length}</b> / {itemsWithPermission.length}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShow(false)}>
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    onClick={handleAction}
                    disabled={validItems.length === 0}
                    className={actionType === 'delete' ? "bg-red-500 hover:bg-red-600" : "bg-orange-500 hover:bg-orange-600"}
                  >
                    {actionType === 'delete' ? (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        <Trans>Delete Available</Trans>
                      </>
                    ) : (
                      <>
                        <FolderOpen className="w-4 h-4 mr-2" />
                        <Trans>Move Available</Trans>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </>
        )}

        {step === 'folder-select' && (
          <>
            <DialogHeader className="p-6 pb-2 border-b">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8 -ml-2" onClick={() => setStep('list')}>
                  <ArrowRight className="w-4 h-4 rotate-180" />
                </Button>
                <DialogTitle><Trans>Select destination folder</Trans></DialogTitle>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto">
              <button onClick={() => handleMoveToFolder(undefined)} className="w-full flex items-center gap-3 px-6 py-4 hover:bg-gray-50 text-left border-b border-gray-100 transition-colors">
                <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                  <FolderOpen className="w-5 h-5" />
                </div>
                <span className="font-medium"><Trans>Top Folder</Trans></span>
              </button>
              {folderItems.map(fItem => (
                <button key={fItem.item.id} onClick={() => handleMoveToFolder(fItem)} className="w-full flex items-center gap-3 px-6 py-4 hover:bg-gray-50 text-left border-b border-gray-100 transition-colors pl-10">
                  <div className="p-2 bg-orange-50 rounded-lg text-orange-500">
                    <Folder className="w-5 h-5" />
                  </div>
                  <span className="font-medium">{fItem.item.title}</span>
                </button>
              ))}
            </div>

            <div className="p-4 border-t bg-gray-50 text-right">
              <Button variant="outline" onClick={() => setShow(false)}><Trans>Cancel</Trans></Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CheckedItemModal;
